import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from '../../../store/rootReducer';
import { fetchBlueprintByIdThunk, updateBlueprintThunk } from '../../../store/blueprints/blueprintsActions';
import { BlueprintDraft } from '../../../store/blueprints/blueprintsTypes';
import { validateBlueprintDraft } from '../../../store/blueprints/blueprintsValidation';
import { hasUserAdminRoles } from '../../../utils/bzmUserConfigUtil';
import BlueprintMetaFields from './BlueprintMetaFields';
import { BLUEPRINTS_ROOT } from './blueprintRoutes';

const BlueprintEditView = () => {
  const { t } = useTranslation('blueprints');
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((s: ApplicationState) => s.blueprints);
  const blazeUserConfig = useSelector((s: ApplicationState) => s.blazeUserConfig.blazeUserConfig);
  const canPublishAccount = hasUserAdminRoles(blazeUserConfig);

  const blueprint = state.blueprints.find(b => b.id === id) || null;

  const [draft, setDraft] = useState<BlueprintDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchBlueprintByIdThunk(id) as never);
  }, [dispatch, id]);

  useEffect(() => {
    if (blueprint) {
      setDraft({
        id: blueprint.id,
        name: blueprint.name,
        description: blueprint.description,
        version: blueprint.version,
        tags: blueprint.tags,
        thumbnail: blueprint.thumbnail,
        transactionIds: blueprint.transactions.filter(x => x.id != null).map(x => x.id as number),
        visibility: blueprint.visibility,
      });
    }
    // re-run only when the blueprint id changes, not on every store update
  }, [blueprint?.id]);

  const detailPath = `${BLUEPRINTS_ROOT}/${id}`;

  if (!blueprint || !draft) {
    if (state.isLoading) {
      return (
        <div className="card">
          <div className="card-body text-muted">{t('detail.loading')}</div>
        </div>
      );
    }
    return (
      <div className="card">
        <div className="card-body">
          <button type="button" className="btn btn-link p-0 mb-3" onClick={() => history.push(BLUEPRINTS_ROOT)}>
            {t('detail.back')}
          </button>
          <p>{t('detail.notFound')}</p>
        </div>
      </div>
    );
  }

  const update = (patch: Partial<BlueprintDraft>) => setDraft(d => (d ? { ...d, ...patch } : d));

  const handleSave = async () => {
    const validationError = validateBlueprintDraft(draft, t, { requireTransactions: false });
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    const ok = ((await dispatch(updateBlueprintThunk(blueprint.id, draft) as never)) as unknown) as boolean;
    if (ok) history.push(detailPath);
  };

  return (
    <div className="card">
      <div className="card-body">
        <h4 className="mb-3">{t('edit.title')}</h4>

        <BlueprintMetaFields draft={draft} onChange={update} canPublishAccount={canPublishAccount} twoColumn showVisibility={false} />

        {error && <div className="text-danger mt-3">{error}</div>}

        <div className="d-flex justify-content-end mt-3" style={{ gap: 8 }}>
          <button type="button" className="btn btn-link" onClick={() => history.push(detailPath)}>
            {t('edit.cancel')}
          </button>
          <button type="button" data-test="bp-save" className="btn btn-primary" onClick={handleSave}>
            {t('edit.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlueprintEditView;
